import { Box } from "@mui/material";
import { useContext, useMemo } from "react";
import CustomButton from "../../UIElements/CustomButton";
import "../profil-styles.scss";
import { useForm, Controller } from "react-hook-form";
import { yupResolver } from "@hookform/resolvers/yup";
import * as yup from "yup";
import { passwordRegex, sessionStorageKey } from "../../../consts";
import PasswordInput from "../../UIElements/PasswordInput/PasswordInput";
import PasswordRequirementsList from "../../Common/PasswordRequirementsList";
import CancelModalButton from "../../UIElements/CustomModal/CancelModalButton";
import { useLocalStorage } from "../../../hooks";
import { Session } from "../../../types";
import { AuthContext } from "../../../contexts";
import { changePassword } from "../../../services/auth/password.service";

const passwordNotValideMsg = "Veuillez renseigner un mot de passe valide";

const validationSchema = yup.object().shape({
    currentPassword: yup.string().required("Mot de passe actuel requis"),
    newPassword: yup
        .string()
        .matches(passwordRegex, passwordNotValideMsg)
        .notOneOf(
            [yup.ref("currentPassword"), null],
            "Le nouveau mot de passe ne peut pas être le même que l'ancien"
        )
        .required("Nouveau mot de passe requis"),
    confirmPassword: yup
        .string()
        .oneOf(
            [yup.ref("newPassword")],
            "Les mots de passe ne correspondent pas"
        )
        .required("Confirmation du mot de passe requise"),
});

export default function EditPasswordForm() {
    const {
        handleSubmit,
        formState: { errors },
        control,
        watch,
    } = useForm({
        defaultValues: {
            currentPassword: "",
            newPassword: "",
            confirmPassword: "",
        },
        resolver: yupResolver(validationSchema),
    });

    const currentPassword = watch("currentPassword");
    const newPassword = watch("newPassword");
    const confirmedPassword = watch("confirmPassword");

    const isValidCurrentPassword = useMemo(
        () => passwordRegex.test(currentPassword ?? ""),
        [currentPassword]
    );

    const isValidNewPassword = useMemo(
        () => passwordRegex.test(newPassword ?? ""),
        [newPassword]
    );

    const { storedValue: session } =
        useLocalStorage<Session>(sessionStorageKey);

    const {
        auth: { user: currentUser },
    } = session;

    const { logout } = useContext(AuthContext);

    const onSubmit = async (data: any) => {
        if (data) {
            const changingPaswd = await changePassword({
                password: data.currentPassword,
                new_password: data.newPassword,
                email: currentUser.email,
            });
            if (!changingPaswd.detail) {
                logout();
            }
        }
    };

    return (
        <Box className="profil-form-root">
            <form
                onSubmit={handleSubmit(onSubmit)}
                className="profil-form-container"
            >
                <Controller
                    name="currentPassword"
                    control={control}
                    render={({ field: { ref, ...others } }) => (
                        <PasswordInput
                            fullWidth={true}
                            label={"Mot de passe actuel *"}
                            {...others}
                            havePasswordChecker={true}
                            isValidPassword={isValidCurrentPassword}
                            errortext={errors.currentPassword?.message ?? ""}
                            showvisibility={(currentPassword || "").length}
                            bordersx={{
                                border: "2px solid  #E3E6E8",
                            }}
                        />
                    )}
                />
                <Controller
                    name="newPassword"
                    control={control}
                    render={({ field: { ref, ...others } }) => (
                        <PasswordInput
                            fullWidth={true}
                            label={"Nouveau mot de passe *"}
                            {...others}
                            havePasswordChecker={true}
                            isValidPassword={isValidNewPassword}
                            errortext={errors.newPassword?.message ?? ""}
                            showvisibility={(newPassword || "").length}
                            bordersx={{
                                border: "2px solid  #E3E6E8",
                            }}
                        />
                    )}
                />
                <Controller
                    name="confirmPassword"
                    control={control}
                    render={({ field: { ref, ...others } }) => (
                        <PasswordInput
                            fullWidth={true}
                            label={"Confirmation de mot de passe *"}
                            {...others}
                            havePasswordChecker={true}
                            errortext={errors.confirmPassword?.message ?? ""}
                            showvisibility={(confirmedPassword || "").length}
                            bordersx={{
                                border: "2px solid  #E3E6E8",
                            }}
                        />
                    )}
                />
                <PasswordRequirementsList password={newPassword} />
                <Box
                    sx={{
                        display: "flex",
                        flexDirection: "column",
                        alignItems: "flex-start",
                        gap: "1rem",
                        alignSelf: "stretch",
                    }}
                >
                    <CustomButton
                        type="submit"
                        label="Enregistrer"
                        customsx={{
                            width: "100%",
                        }}
                    />
                    <CancelModalButton
                        customsx={{
                            width: "100%",
                        }}
                    />
                </Box>
            </form>
        </Box>
    );
}
